import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import CotizacionForm from "./CotizacionForm";
import Paginador from "./Paginador";
import { getFirestore, collection, onSnapshot } from "firebase/firestore";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

Modal.setAppElement("#root");

const BandejaCotizaciones = ({
  cotizaciones,
  setCotizaciones,
  selectedCotizacionId,
  onRowClick,
  guardarCotizacion,
  cotizacion,
  clientes,
}) => {
  const [filteredCotizaciones, setFilteredCotizaciones] =
    useState(cotizaciones);
  const [showModal, setShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOptions, setShowOptions] = useState(false); // Opciones del botón de "Nuevo"
  const [showBulkActionsOptions, setShowBulkActionsOptions] = useState(false); // Opciones de "Marcar como enviado"
  const [selectedCotizaciones, setSelectedCotizaciones] = useState([]); // Estado para las cotizaciones seleccionadas
  const [selectAll, setSelectAll] = useState(false); // Checkbox genérico (seleccionar todos)
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const { formatDate } = useFunctionsPrevia();

  useEffect(() => {
    const firestore = getFirestore();
    const cotizacionesRef = collection(firestore, "cotizaciones");

    const unsubscribe = onSnapshot(cotizacionesRef, (snapshot) => {
      const cotizacionesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Ordenar las cotizaciones por fecha de creación (más recientes primero)
      cotizacionesData.sort(
        (a, b) => new Date(b.fechaCotizacion) - new Date(a.fechaCotizacion)
      );

      setCotizaciones(cotizacionesData);
      setFilteredCotizaciones(cotizacionesData); // Actualizar la lista filtrada también
    });

    return () => unsubscribe(); // Limpieza del listener al desmontar el componente
  }, [setCotizaciones]);

  const totalPages = Math.ceil(filteredCotizaciones.length / itemsPerPage);

  const paginatedCotizaciones = filteredCotizaciones.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reinicia a la primera página
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    const results = [];

    cotizaciones.forEach((cotizacion) => {
      let matchScore = 0;

      // 1) Estado (con +2 si manejas "estado" en cotizaciones)
      if ((cotizacion.estado || "").toLowerCase().includes(searchTerm)) {
        matchScore += 2;
      }

      // 2) Nombre del cliente (+1)
      if ((cotizacion.nombreCliente || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }

      // 3) Número de cotización (con o sin "IC-") (+1)
      let numeroStr = (cotizacion.numeroCotizacion || "").toLowerCase();
      let numeroWithoutPrefix = numeroStr.replace(/^ic-/, "");
      if (
        numeroStr.includes(searchTerm) ||
        numeroWithoutPrefix.includes(searchTerm)
      ) {
        matchScore += 1;
      }

      // 4) Fecha de cotización (string original) (+1)
      if (
        (cotizacion.fechaCotizacion || "").toLowerCase().includes(searchTerm)
      ) {
        matchScore += 1;
      }

      // 5) Formatos alternativos de fecha (+1 si alguno coincide)
      if (cotizacion.fechaCotizacion) {
        const dateFormats = formatDate(
          new Date(cotizacion.fechaCotizacion),
          true
        );
        if (dateFormats.some((format) => format.includes(searchTerm))) {
          matchScore += 1;
        }
      }

      if (cotizacion.fechaVencimiento) {
        const fecha = new Date(cotizacion.fechaVencimiento);
        console.log(
          "Fecha vencimiento:",
          cotizacion.fechaVencimiento,
          fecha.toString()
        );
        const dateFormats = formatDate(fecha, true);
        if (dateFormats.some((format) => format.includes(searchTerm))) {
          matchScore += 1;
        }
      }
      // 6) Importe formateado (con +2 para destacar)
      const formattedImporte = parseFloat(cotizacion?.total || 0)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      if (formattedImporte.includes(searchTerm)) {
        matchScore += 2;
      }
      // 7) Otros campos (area, asunto, atencion, cliente, createdAt) (+1)
      if ((cotizacion.area || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.referencia || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.divisa || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.asunto || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.terminos || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.atencion || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if (
        (cotizacion.vendedorNombre || "").toLowerCase().includes(searchTerm)
      ) {
        matchScore += 1;
      }
      if (
        (cotizacion.vendedorCorreo || "").toLowerCase().includes(searchTerm)
      ) {
        matchScore += 1;
      }
      if ((cotizacion.cliente || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.notas || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((cotizacion.planta || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      // Si el matchScore > 0, esta cotización coincide con el término
      if (matchScore > 0) {
        results.push({ ...cotizacion, matchScore });
      }
    });

    // Ordenamos los resultados por puntaje descendente
    results.sort((a, b) => b.matchScore - a.matchScore);

    setFilteredCotizaciones(results);
    setCurrentPage(1);
  };

  const openFormulario = () => {
    setIsFormDirty(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (isFormDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          setShowModal(false);
          setIsFormDirty(false); // Restablecer el estado formDirty
        }
      });
    } else {
      setShowModal(false);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleBulkActionsOptions = () => {
    setShowBulkActionsOptions(!showBulkActionsOptions);
  };

  const handleSelectCotizacion = (cotizacionId) => {
    setSelectedCotizaciones((prevSelected) =>
      prevSelected.includes(cotizacionId)
        ? prevSelected.filter((id) => id !== cotizacionId)
        : [...prevSelected, cotizacionId]
    );
  };

  // Checkbox genérico - seleccionar todos
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCotizaciones([]); // Deselecciona todo
    } else {
      setSelectedCotizaciones(cotizaciones.map((cot) => cot.id)); // Selecciona todo
    }
    setSelectAll(!selectAll);
  };

  const showBulkActions = selectedCotizaciones.length > 0;

  const exportarPDF = () => {
    console.log("Exportar como PDF");
  };

  const imprimir = () => {
    console.log("Imprimir cotización");
  };

  const eliminar = () => {
    console.log("Eliminar cotización");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".bulk-actions-right")) {
        setShowBulkActionsOptions(false);
      }
    };
    if (showBulkActionsOptions) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showBulkActionsOptions]);

  return (
    <div className="cotizaciones-bandeja">
      <div className="bandeja-search-form">
        <div className="bandeja-search-input-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="bandeja-search-input"
            placeholder="Buscar..."
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* Mostrar encabezado condicional con las opciones de acciones en lote */}
      {showBulkActions && (
        <div className="bulk-actions">
          <div className="bulk-actions-left">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              style={{ marginTop: "0", alignSelf: "center" }} // Ajuste para centrar el checkbox
            />
            <p>{selectedCotizaciones.length} seleccionados</p>
          </div>

          <div className="bulk-actions-right-container">
            <button className="boton-accion">Marcar como enviado</button>
            <div
              className="bulk-actions-right"
              style={{ position: "relative" }}
            >
              <i
                className="bi bi-three-dots-vertical"
                onClick={toggleBulkActionsOptions}
                style={{ cursor: "pointer" }}
              ></i>
              {showBulkActionsOptions && (
                <div className="dropdown-options-bandeja">
                  <p onClick={exportarPDF}>
                    <i
                      style={{ fontSize: "14px" }}
                      className="bi bi-box-arrow-up-right"
                    ></i>
                    Exportar como PDF
                  </p>
                  <p onClick={imprimir}>
                    <i
                      style={{ fontSize: "14px" }}
                      className="bi bi-printer"
                    ></i>
                    Imprimir
                  </p>
                  <p onClick={eliminar}>
                    <i style={{ fontSize: "14px" }} className="bi bi-trash"></i>
                    Eliminar
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="encabezado">
        <div className="categoria-filtro" onClick={toggleDropdown}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <p style={{ marginLeft: "10px" }}>Todas las cotizaciones</p>
            <i
              className="bi bi-chevron-down"
              style={{ color: "#007bff", fontWeight: "bold" }}
            ></i>
          </div>
          {showDropdown && (
            <div className="dropdown-menu">
              <p>
                <i className="bi bi-envelope-check"></i>Cotizaciones enviadas
              </p>
              <p>
                <i className="bi bi-clock"></i>Cotizaciones pendientes
              </p>
            </div>
          )}
        </div>
        <div className="acciones-encabezado" style={{ position: "relative" }}>
          <button className="boton-accion" onClick={openFormulario}>
            + Nuevo
          </button>
          <div style={{ position: "relative", display: "inline-block" }}>
            <i
              className="bi bi-three-dots-vertical"
              onClick={toggleOptions}
              style={{ cursor: "pointer" }}
            ></i>
            {showOptions && (
              <div className="dropdown-options-bandeja">
                <p onClick={() => console.log("Ordenar")}>
                  <i className="bi bi-arrow-down-up"></i>Ordenar
                </p>
                <p onClick={() => console.log("Importar")}>
                  <i className="bi bi-box-arrow-in-down"></i>Importar
                </p>
                <p onClick={() => console.log("Exportar")}>
                  <i className="bi bi-box-arrow-up"></i>Exportar
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {paginatedCotizaciones.length > 0 ? (
        paginatedCotizaciones.map((cotizacion) => (
          <div
            key={cotizacion.id}
            className={`mensaje-cotizacion ${
              selectedCotizacionId === cotizacion.id
                ? "mensaje-seleccionado"
                : ""
            }`}
            onClick={() => {
              onRowClick(cotizacion.id);
            }}
            tabIndex="0"
          >
            <div className="info-container">
              <input
                type="checkbox"
                onChange={() => handleSelectCotizacion(cotizacion.id)}
                checked={selectedCotizaciones.includes(cotizacion.id)}
              />
              <div className="info-column">
                <div className="cliente-importe">
                  <span className="cliente-nombre">
                    {cotizacion.nombreCliente}
                  </span>
                  <span className="importe">
                    {cotizacion.divisa}{" "}
                    {parseFloat(cotizacion?.total)
                      ?.toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
                <div className="fecha-referencia">
                  <p>
                    {" "}
                    {cotizacion.numeroCotizacion.toString().padStart(4, "0")}
                  </p>
                  <span className="punto">•</span>
                  <p>{formatDate(new Date(cotizacion.fechaCotizacion))}</p>
                </div>
                <div style={{ marginTop: "5px" }}>
                  <div
                    className={`status ${
                      cotizacion.estado === "Pendiente"
                        ? "pendiente"
                        : cotizacion.estado === "En Proceso"
                        ? "en-proceso"
                        : cotizacion.estado === "Facturado"
                        ? "facturado"
                        : "pagada" // Clase por defecto para el estado "Pagada"
                    }`}
                  >
                    {cotizacion.estado}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div style={{ position: "relative", left: "5%", marginTop: "80px" }}>
          <p style={{ fontSize: "14px" }}>
            No existe ninguna cotización que coincida con esta búsqueda.
          </p>

          <div className="sin-resultados-animation" style={{ left: "10%" }}>
            <img
              src="/img/noSearch.svg"
              alt="Sin resultados"
              className="sin-resultados-imagen"
            />
          </div>
        </div>
      )}

      <div className="paginador">
        <div style={{ paddingLeft: "10px", width: "34%" }}>
          <Paginador
            totalItems={filteredCotizaciones.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        contentLabel="Nueva Cotización"
        style={{
          overlay: {
            zIndex: 2,
          },
          content: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            paddingLeft: 0,
            padding: 0,
            fontFamily: "Roboto, sans-serif",
            "::WebkitScrollbar": {
              width: "0px",
              background: "transparent",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          },
        }}
      >
        <button onClick={closeModal} className="cerrar-button">
          <i className="bi bi-x-lg"></i>
        </button>

        <CotizacionForm
          clientes={clientes}
          guardarCotizacion={guardarCotizacion}
          cotizacion={cotizacion}
          onFormDirtyChange={(dirty) => setIsFormDirty(dirty)}
        />
      </Modal>
    </div>
  );
};

export default BandejaCotizaciones;
